import { useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'

export default function Subscribe() {
  const { priceId } = useParams()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    // Require login
    if (!user) {
      console.log('[Subscribe] No user found, redirecting to /login')
      navigate('/login')
      return
    }

    // Capture referral code from URL if present (and persist for checkout)
    const refFromUrl = searchParams.get('ref')
    if (refFromUrl) {
      try {
        localStorage.setItem('referrerId', refFromUrl)
        console.log('[Subscribe] Stored referral from URL:', refFromUrl)
      } catch {}
    }

    const sessionId = searchParams.get('session_id')
    const incomingTraceId = searchParams.get('t')
    console.log('[Subscribe] route params:', { priceId, userId: user?.id, sessionId, incomingTraceId })
    // If Stripe redirected back with session_id, always confirm subscription (server is idempotent)
    if (sessionId) {
      (async () => {
        try {
          console.log('[Subscribe] Confirming subscription for sessionId:', sessionId, 'traceId:', incomingTraceId)
          const controller = new AbortController()
          const timeout = setTimeout(() => controller.abort(), 10000)
          const res = await fetch('https://backend.amply.buildfast.us/confirm-subscription', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessionId, traceId: incomingTraceId || null }),
            signal: controller.signal,
          })
          clearTimeout(timeout)
          console.log('[Subscribe] confirm-subscription status:', res.status)
          let data = null
          try { data = await res.json() } catch {}
          if (!data?.success) {
            console.error('[Subscribe] Confirm-subscription failed:', data?.error)
          }
          console.log('[Subscribe] Confirm-subscription response:', data)
        } catch (err) {
          console.error('[Subscribe] Error confirming subscription (will still navigate):', err)
        } finally {
          navigate('/settings')
        }
      })()
      return
    }

    async function processSubscription() {
      // Helper to start checkout (with referral support)
      const startCheckout = async (stripePriceId) => {
        const ref = (typeof window !== 'undefined' && localStorage.getItem('referrerId')) || ''
        const traceId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
        console.log('[Subscribe] Starting checkout with:', { stripePriceId, userId: user.id, ref, traceId })
  const res = await fetch('https://backend.amply.buildfast.us/create-checkout-session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ priceId: stripePriceId, userId: user.id, ref, traceId }),
        })
        console.log('[Subscribe] create-checkout-session status:', res.status)
        const { url, error } = await res.json()
        console.log('[Subscribe] create-checkout-session response:', { url, error })
        if (error || !url) throw new Error(error || 'Failed to create checkout session')
        window.location.href = url
      }

      try {
        console.log('[Subscribe] Fetching plan by stripe_price_id:', priceId)
        // Try to fetch plan details from DB (optional, for free-tier logic/meta)
        const { data: plan, error: planErr } = await supabase
          .from('plans')
          .select('*')
          .eq('stripe_price_id', priceId)
          .maybeSingle()
        console.log('[Subscribe] Plan lookup result:', { plan, planErr })

        if (plan && plan.price_cents === 0) {
          // Free plan: upsert subscription directly
          const { error: freeErr } = await supabase
            .from('subscriptions')
            .upsert({ user_id: user.id, plan_id: plan.id, status: 'active' })
          if (freeErr) {
            console.error('[Subscribe] Free subscription error:', freeErr)
            alert('Failed to activate free plan. Please try again later.')
          } else {
            alert('Subscribed to free plan!')
          }
          navigate('/settings')
          return
        }

        // Paid plan: start checkout; if plan not found, fall back to route priceId directly
        const stripePriceId = plan?.stripe_price_id || priceId
        console.log('[Subscribe] Proceeding to paid checkout with priceId:', stripePriceId)
        await startCheckout(stripePriceId)
      } catch (err) {
        console.error('[Subscribe] Subscription flow error:', err)
        alert('Could not start checkout. Please try again or contact support.')
        navigate('/pricing')
      }
    }

    processSubscription()
  }, [priceId, user, searchParams, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white">
      <p>Processing your subscription...</p>
    </div>
  )
}
